// 운영 서랍 「회원 화면 보기」 순수 로직 (2026-10-20 시안 C · docs/ops/admin-view-as-drawer-plan.md)
//
// 홈·영상권리사 상단바의 「다른 사용자 이메일」 입력을 대신한다 — 이름·닉네임·이메일·회사로 찾고,
// 최근 본 회원 5명을 이 브라우저에만 기억한다. 열기 자체는 종전과 같이 `impersonateEmail` 을 쓰므로
// (UserContext 의 Admin 재확인 · ImpersonateNotice · 로그아웃 삭제) 여기서는 저장 방식을 바꾸지 않는다.

export const RECENT_KEY = "impersonateRecent";
export const RECENT_MAX = 5;
export const SEARCH_MIN = 2;
export const SEARCH_LIMIT = 8;

/** 결과 행·요약 카드가 읽는 열 — 전화번호는 일부러 넣지 않는다(기획 §2 「결과 한 줄」). */
export const USER_SELECT =
  "id, name, email, nick_name, company, role, verified, approved_at, created_date, signup_ref, grade, is_not_contract, deleted_at";

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** 최근 본 회원 — 최신순 `[{ email, name, at }]`. 못 읽으면 빈 배열. */
export function readRecent(storage = safeStorage()) {
  if (!storage) return [];
  try {
    const raw = storage.getItem(RECENT_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list
      .filter((r) => r && typeof r.email === "string" && r.email)
      .map((r) => ({ email: r.email, name: typeof r.name === "string" ? r.name : "", at: Number(r.at) || 0 }))
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecent(list, storage) {
  if (!storage) return list;
  try {
    storage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* 저장 못 해도 화면은 그대로 */
  }
  return list;
}

/** 맨 앞에 넣고(같은 이메일은 끌어올림) 5명까지만 남긴다. 돌려주는 값은 새 목록. */
export function addRecent(entry, storage = safeStorage(), now = Date.now()) {
  const email = typeof entry?.email === "string" ? entry.email.trim().toLowerCase() : "";
  if (!email) return readRecent(storage);
  const rest = readRecent(storage).filter((r) => r.email.toLowerCase() !== email);
  const next = [{ email, name: entry.name || "", at: now }, ...rest].slice(0, RECENT_MAX);
  return writeRecent(next, storage);
}

export function removeRecent(email, storage = safeStorage()) {
  const key = String(email || "").toLowerCase();
  const next = readRecent(storage).filter((r) => r.email.toLowerCase() !== key);
  return writeRecent(next, storage);
}

export function clearRecent(storage = safeStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(RECENT_KEY);
  } catch {
    /* noop */
  }
}

/** 입력을 PostgREST `or()` 필터로 — 2글자 미만이면 null. 필터 문법 글자(쉼표·괄호·%) 는 뺀다. */
export function searchFilter(query) {
  const q = String(query || "").trim().replace(/[,()%*]/g, "");
  if (q.length < SEARCH_MIN) return null;
  const like = `%${q}%`;
  return ["name", "email", "nick_name", "company"].map((col) => `${col}.ilike.${like}`).join(",");
}

/** 「@」 가 있고 공백이 없으면 이메일로 본다 — 결과가 없을 때 「이 이메일로 바로 열기」 줄을 보여 줄지. */
export function looksLikeEmail(query) {
  const q = String(query || "").trim();
  return q.includes("@") && !/\s/.test(q) && q.indexOf("@") > 0;
}

/** 일치 부분 강조용 — `[{ text, hit }]`. 대소문자 무시, 첫 일치만. */
export function highlightParts(text, query) {
  const t = String(text ?? "");
  const q = String(query || "").trim().replace(/[,()%*]/g, "");
  if (!t) return [];
  if (!q) return [{ text: t, hit: false }];
  const idx = t.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return [{ text: t, hit: false }];
  const parts = [];
  if (idx > 0) parts.push({ text: t.slice(0, idx), hit: false });
  parts.push({ text: t.slice(idx, idx + q.length), hit: true });
  if (idx + q.length < t.length) parts.push({ text: t.slice(idx + q.length), hit: false });
  return parts;
}

/** 결과 행 제목 — 이름이 없으면 닉네임, 그것도 없으면 이메일 앞부분. */
export function displayName(user) {
  if (!user) return "";
  if (user.name && String(user.name).trim()) return String(user.name).trim();
  if (user.nick_name && String(user.nick_name).trim()) return String(user.nick_name).trim();
  const email = String(user.email || "");
  return email.includes("@") ? email.slice(0, email.indexOf("@")) : email;
}

/** 역할 배지 — 크리에이터가 아닌 역할만 보인다(파트너 · 영상권리사 · 운영자). */
export function roleBadges(role) {
  const r = String(role || "");
  const out = [];
  if (r.includes("Partner")) out.push("파트너");
  if (r.includes("Copyrights Holder")) out.push("영상권리사");
  if (r.includes("Admin")) out.push("운영자");
  return out;
}
